import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Briefcase, MapPin, Clock, Flag, ShieldCheck, Send, ArrowLeft, CheckCircle2, Users, Wrench, UserSearch, ExternalLink, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CitySelect } from "@/components/CitySelect";
import { MrDriveAvatar } from "@/components/MrDriveAvatar";
import { ShareWorkButton } from "@/components/ShareWorkButton";
import { EXPERT_CATEGORIES, isExpert, primaryContact } from "@/lib/expert";
import { PriveConciergeBanner, privePalette } from "@/components/PriveConciergeBanner";
import { normalizeHref, telegramHref } from "@/lib/social-links";
import { CURRENCY_OPTIONS } from "@/lib/marketplace-constants";
import { conciergeGeneralHref, conciergePostHref } from "@/lib/concierge";
import {
  EMPLOYMENT_LABEL, PAY_PERIOD_LABEL, WORK_CATEGORIES, expertCategoriesForWork, WORK_STATUS_LABEL, daysAgo, daysLeft, formatDeadline, formatPay, placeLabel,
  type WorkKind, type WorkPost, type WorkSide,
} from "@/lib/work";

const db = supabase as any;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const truthy = (v: unknown) => v === true || v === 1 || v === "1" || v === "true";

type WorkSearch = {
  tab?: "vacancies" | "orders";
  post?: string;
  responses?: boolean;
  new?: boolean;
  mine?: boolean;
};

export const Route = createFileRoute("/_authenticated/work")({
  validateSearch: (s: Record<string, unknown>): WorkSearch => ({
    tab: s.tab === "orders" ? "orders" : "vacancies",
    post: typeof s.post === "string" && UUID_RE.test(s.post) ? s.post : undefined,
    responses: truthy(s.responses) ? true : undefined,
    new: truthy(s.new) ? true : undefined,
    mine: truthy(s.mine) ? true : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Работа — TurkeyDrive" },
      { name: "description", content: "Вакансии и заказы среди своих в Турции: вакансии и заказы." },
      { property: "og:title", content: "Работа — TurkeyDrive" },
      { property: "og:description", content: "Вакансии и заказы среди своих в Турции: найди человека или работу." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: WorkPage,
});

type MiniProfile = {
  user_id: string;
  full_name: string;
  photo_url: string | null;
  city: string | null;
  username?: string | null;
  telegram?: string | null;
  social_links?: string[] | null;
};

type MyProfile = MiniProfile & { is_admin: boolean };

const btnPrimary = "inline-flex h-11 min-h-11 min-w-0 items-center justify-center gap-1.5 self-center whitespace-nowrap rounded-full bg-td-teal px-5 text-sm font-semibold text-white transition hover:brightness-105 disabled:opacity-50";
const btnSecondary = "inline-flex h-11 min-h-11 min-w-0 items-center justify-center gap-1.5 self-center whitespace-nowrap rounded-full border border-td-teal px-4 text-sm font-semibold text-td-teal transition hover:bg-td-teal/5 disabled:opacity-50";
const chip = (on: boolean) =>
  "min-h-9 shrink-0 rounded-full border px-3 py-1.5 text-xs font-medium transition " +
  (on ? "border-td-teal bg-td-teal text-white" : "border-td-line bg-white text-td-dark hover:border-td-teal/60");

function withTextParam(url: string, text: string): string {
  return `${url}${url.includes("?") ? "&" : "?"}text=${encodeURIComponent(text)}`;
}

function Avatar({ p, size = 28 }: { p: { photo_url?: string | null; full_name?: string | null } | null | undefined; size?: number }) {
  return p?.photo_url ? (
    <img loading="lazy" decoding="async" src={p.photo_url} alt="" className="shrink-0 rounded-full object-cover" style={{ width: size, height: size }} />
  ) : (
    <span className="flex shrink-0 items-center justify-center rounded-full bg-td-soft text-[11px] font-semibold text-td-dark" style={{ width: size, height: size }}>
      {(p?.full_name ?? "?").slice(0, 1)}
    </span>
  );
}

function useMyProfile() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["work", "me", user?.id],
    enabled: !!user,
    queryFn: async (): Promise<MyProfile | null> => {
      const { data, error } = await db
        .from("profiles")
        .select("user_id, full_name, photo_url, city, username, telegram, social_links, is_admin")
        .eq("user_id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

function WorkPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { user } = useAuth();
  const me = useMyProfile().data ?? null;
  const isAdmin = !!me?.is_admin;
  const [editing, setEditing] = useState<WorkPost | null>(null);
  const [done, setDone] = useState<WorkPost | null>(null);
  const setSearch = (patch: Partial<WorkSearch>) =>
    navigate({ to: "/work", search: (s: any) => ({ ...s, ...patch }) as any, replace: true });

  if (done) {
    return (
      <SuccessScreen
        post={done}
        me={me}
        onMine={() => { setDone(null); navigate({ to: "/work", search: { tab: done.kind === "order" ? "orders" : "vacancies", mine: true } }); }}
        onAnother={() => { setDone(null); navigate({ to: "/work", search: { tab: search.tab, new: true } }); }}
      />
    );
  }

  if (search.new || editing) {
    return (
      <PostForm
        me={me}
        initial={editing}
        onCancel={() => { setEditing(null); setSearch({ new: undefined }); }}
        onSaved={(p, created) => {
          setEditing(null);
          if (created) { setDone(p); setSearch({ new: undefined }); }
          else { setSearch({ new: undefined, post: p.id }); }
        }}
      />
    );
  }

  return <Board me={me} isAdmin={isAdmin} userId={user?.id ?? null} onEdit={(p) => { setSearch({ post: undefined, responses: undefined }); setEditing(p); }} />;
}

/* ───────────────────────────── Доска ───────────────────────────── */

function Board({ me, isAdmin, userId, onEdit }: { me: MyProfile | null; isAdmin: boolean; userId: string | null; onEdit: (p: WorkPost) => void }) {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [category, setCategory] = useState<string>("");
  const [city, setCity] = useState("");
  const [format, setFormat] = useState<"any" | "online" | "offline">("any");
  const mine = !!search.mine;
  const [showHidden, setShowHidden] = useState(false);
  const hiddenOnly = isAdmin && showHidden && !mine;

  const setSearch = (patch: Partial<WorkSearch>) =>
    navigate({ to: "/work", search: (s: any) => ({ ...s, ...patch }), replace: true } as any);
  const setMine = (v: boolean) => setSearch({ mine: v ? true : undefined });

  const posts = useQuery({
    queryKey: ["work", "posts", userId],
    queryFn: async (): Promise<WorkPost[]> => {
      let qb = db.from("work_posts").select("*");
      // На запуск показываем только side=need; свои объявления любой стороны остаются в «Мои».
      qb = userId ? qb.or(`side.eq.need,user_id.eq.${userId}`) : qb.eq("side", "need");
      const { data, error } = await qb.order("created_at", { ascending: false }).limit(500);
      if (error) throw error;
      return data ?? [];
    },
  });
  useEffect(() => { if (posts.error) toast.error("Не удалось загрузить объявления"); }, [posts.error]);

  const authorIds = useMemo(() => Array.from(new Set((posts.data ?? []).map((p) => p.user_id))), [posts.data]);
  const authors = useQuery({
    queryKey: ["work", "authors", authorIds.join(",")],
    enabled: authorIds.length > 0,
    queryFn: async (): Promise<Record<string, MiniProfile>> => {
      const { data, error } = await db.from("profiles").select("user_id, full_name, photo_url, city").in("user_id", authorIds);
      if (error) throw error;
      return Object.fromEntries((data ?? []).map((p: MiniProfile) => [p.user_id, p]));
    },
  });

  const [mineView, setMineView] = useState<"posts" | "responses">("posts");

  // Мои отклики: post_id → дата отклика
  const myResponses = useQuery({
    queryKey: ["work", "my-responses", userId],
    enabled: !!userId && mine,
    queryFn: async (): Promise<Record<string, string>> => {
      const { data, error } = await db.from("work_responses").select("post_id, created_at").eq("sender_user_id", userId);
      if (error) throw error;
      return Object.fromEntries((data ?? []).map((r: { post_id: string; created_at: string }) => [r.post_id, r.created_at]));
    },
  });

  // Отклики на мои объявления: число и есть ли новые за 24 часа
  const myPostIds = useMemo(() => (posts.data ?? []).filter((p) => p.user_id === userId).map((p) => p.id), [posts.data, userId]);
  const myPostStats = useQuery({
    queryKey: ["work", "my-post-stats", myPostIds.join(",")],
    enabled: mine && myPostIds.length > 0,
    queryFn: async (): Promise<Record<string, { count: number; fresh: boolean }>> => {
      const { data, error } = await db.from("work_responses").select("post_id, created_at").in("post_id", myPostIds);
      if (error) throw error;
      const out: Record<string, { count: number; fresh: boolean }> = {};
      const dayAgo = Date.now() - 86_400_000;
      for (const r of (data ?? []) as { post_id: string; created_at: string }[]) {
        const s = (out[r.post_id] ??= { count: 0, fresh: false });
        s.count += 1;
        if (new Date(r.created_at).getTime() > dayAgo) s.fresh = true;
      }
      return out;
    },
  });

  const kind: WorkKind = search.tab === "orders" ? "order" : "vacancy";
  const now = Date.now();
  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (posts.data ?? []).filter((p) => {
      if (p.kind !== kind) return false;
      if (hiddenOnly) { if (p.status !== "hidden") return false; }
      else if (mine && mineView === "responses") { if (!myResponses.data?.[p.id]) return false; }
      else if (mine) { if (p.user_id !== userId) return false; }
      else if (p.status !== "active" || new Date(p.expires_at).getTime() <= now) return false;
      if (!mine && p.side !== "need") return false;
      if (category && p.category !== category) return false;
      if (city && format !== "online" && p.city !== city) return false;
      if (format === "online" && !p.is_online) return false;
      if (format === "offline" && p.is_online) return false;
      if (term) {
        const hay = [p.title, p.description, p.city, p.category].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [posts.data, kind, hiddenOnly, mine, mineView, myResponses.data, userId, category, city, format, q, now]);

  const filtersOn = !!(q.trim() || category || (city && format !== "online") || format !== "any" || hiddenOnly);
  const resetFilters = () => { setQ(""); setCategory(""); setCity(""); setFormat("any"); setShowHidden(false); };

  const PAGE = 20;
  const [limit, setLimit] = useState(PAGE);
  useEffect(() => { setLimit(PAGE); }, [kind, hiddenOnly, mine, mineView, category, city, format, q]);
  const shown = list.slice(0, limit);

  const openPost = search.post ? (posts.data ?? []).find((p) => p.id === search.post) ?? null : null;
  useEffect(() => {
    if (search.post && posts.isSuccess && !openPost) {
      toast.error("Это объявление больше недоступно");
      setSearch({ post: undefined, responses: undefined });
    }
  }, [search.post, posts.isSuccess, openPost]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <main className="mx-auto w-full max-w-3xl min-w-0 px-4 py-6 md:px-6">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-td-dark">Работа</h1>
          <p className="mt-1 text-sm text-td-muted">Вакансии и заказы среди своих</p>
        </div>
        <button type="button" className={btnPrimary + " shrink-0"} onClick={() => setSearch({ new: true })}>
          <Plus className="h-4 w-4" /> Разместить
        </button>
      </div>

      <div className="mt-4 grid grid-cols-2 rounded-full bg-td-soft p-1">
        {(["vacancies", "orders"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setSearch({ tab: t })}
            className={"min-h-10 rounded-full text-sm font-semibold transition " + ((search.tab ?? "vacancies") === t ? "bg-white text-td-dark shadow-sm" : "text-td-muted")}
          >
            {t === "vacancies" ? "Вакансии" : "Заказы"}
          </button>
        ))}
      </div>

      <WorkHint kind={kind} />

      <div className="mt-3 flex gap-5 border-b border-td-line">
        {([false, true] as const).map((m) => (
          <button key={String(m)} type="button" onClick={() => { setMine(m); }}
            className={"-mb-px h-10 whitespace-nowrap border-b-2 text-sm font-semibold transition " + (mine === m ? "border-td-teal text-td-teal" : "border-transparent text-td-muted")}>
            {m ? "Мои" : "Все объявления"}
          </button>
        ))}
      </div>

      {!mine && (
        <div className="mt-3 space-y-3">
          <div>
            <p className="mb-1 text-xs text-td-muted">Формат</p>
            <Segment value={format} onChange={(v) => setFormat(v as typeof format)}
              options={[{ value: "any", label: "Любой" }, { value: "online", label: "Онлайн" }, { value: "offline", label: "Офлайн" }]} />
          </div>
        </div>
      )}

      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="relative col-span-2">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-td-muted" />
          <Input className="pl-9" placeholder="Поиск по объявлениям" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Select value={category || "__all__"} onValueChange={(v) => setCategory(v === "__all__" ? "" : v)}>
          <SelectTrigger><SelectValue placeholder="Сфера" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Все сферы</SelectItem>
            {WORK_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        {format === "online" ? <div /> : <CitySelect value={city} onChange={setCity} allTurkeyLabel="Вся Турция" placeholder="Город" className="min-w-0" />}
      </div>

      {isAdmin && !mine && (
        <label className="mt-3 flex items-center gap-2 text-xs text-td-muted">
          <input type="checkbox" className="h-4 w-4 accent-[var(--color-td-teal)]" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} />
          Показать скрытые после жалоб (для модерации)
        </label>
      )}

      {!posts.isLoading && (
        <div className="mt-4 flex items-center justify-between gap-3 text-xs">
          <span className="text-td-muted">Найдено: {list.length}</span>
          {filtersOn && <button type="button" className="font-medium text-td-teal underline underline-offset-2" onClick={resetFilters}>Сбросить фильтры</button>}
        </div>
      )}
      <div className="mt-2 flex flex-col gap-3">
        {posts.isLoading ? (
          [0, 1, 2].map((i) => <div key={i} className="h-32 animate-pulse rounded-2xl bg-td-soft" />)
        ) : list.length === 0 ? (
          filtersOn ? (
          <div className="flex flex-col items-center rounded-2xl border border-td-line bg-white px-4 py-8 text-center">
            <MrDriveAvatar variant="requests" size={96} />
            <p className="mt-4 max-w-sm text-sm text-td-dark">По этим фильтрам ничего не нашлось</p>
            <button type="button" className={btnSecondary + " mt-4"} onClick={resetFilters}>Сбросить фильтры</button>
          </div>
          ) : (
          <div className="flex flex-col items-center rounded-2xl border border-td-line bg-white px-4 py-8 text-center">
            <MrDriveAvatar variant="requests" size={96} />
            <p className="mt-4 max-w-sm text-sm text-td-dark">Здесь пока тихо. Разместите первую вакансию или заказ — свои увидят!</p>
            <button type="button" className={btnPrimary + " mt-4"} onClick={() => setSearch({ new: true })}>
              <Plus className="h-4 w-4" /> Разместить
            </button>
          </div>
          )
        ) : (
          shown.map((p, i) => (
            <div key={p.id} className="contents">
              <PostCard
                post={p} author={authors.data?.[p.user_id]} showStatus={mine || hiddenOnly} onOpen={() => setSearch({ post: p.id })}
                respondedAt={mine && mineView === "responses" ? myResponses.data?.[p.id] : undefined}
                responseCount={mine && mineView === "posts" ? myPostStats.data?.[p.id]?.count : 0}
                hasNew={!!myPostStats.data?.[p.id]?.fresh}
              />
              {(i === 2 || (list.length < 3 && i === list.length - 1)) && <PriveConciergeBanner href={conciergeGeneralHref(me)} />}
            </div>
          ))
        )}
        {!posts.isLoading && list.length === 0 && <PriveConciergeBanner href={conciergeGeneralHref(me)} />}
        {list.length > limit && (
          <button type="button" className={btnSecondary + " self-center"} onClick={() => setLimit((n) => n + PAGE)}>Показать ещё</button>
        )}
      </div>

      {openPost && (
        <PostDialog
          post={openPost}
          author={authors.data?.[openPost.user_id]}
          me={me}
          userId={userId}
          isAdmin={isAdmin}
          startWithResponses={!!search.responses}
          onClose={() => setSearch({ post: undefined, responses: undefined })}
          onEdit={() => onEdit(openPost)}
        />
      )}
    </main>
  );
}

function Segment({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <div role="radiogroup" className="grid rounded-full bg-td-soft p-1" style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}>
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={value === o.value} onClick={() => onChange(o.value)}
          className={"h-8 min-w-0 truncate whitespace-nowrap rounded-full px-1 text-[13px] font-semibold transition " + (value === o.value ? "bg-td-teal text-white" : "text-td-muted")}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function PostCard({ post, author, showStatus, onOpen, respondedAt, responseCount = 0, hasNew = false }: {
  post: WorkPost; author?: MiniProfile; showStatus: boolean; onOpen: () => void;
  respondedAt?: string; responseCount?: number; hasNew?: boolean;
}) {
  const place = placeLabel(post);
  const deadline = formatDeadline(post.deadline);
  return (
    <div role="button" tabIndex={0} onClick={onOpen} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }} className="relative block w-full min-w-0 cursor-pointer rounded-2xl border border-td-line bg-white p-4 text-left transition hover:border-td-teal/60">
      {isShareable(post) && (
        <div className="absolute right-3 top-3" onClick={(e) => e.stopPropagation()}>
          <ShareWorkButton postId={post.id} kind={post.kind} title={post.title} variant="icon" />
        </div>
      )}
      <div className="flex flex-wrap items-center gap-1.5 pr-9">
        <span className="rounded-full bg-td-teal/10 px-2 py-0.5 text-[11px] font-medium text-td-teal">{post.kind === "vacancy" ? "Вакансия" : "Заказ"}</span>
        {showStatus && !respondedAt && <span className="rounded-full bg-td-orange/10 px-2 py-0.5 text-[11px] font-medium text-td-orange">{post.status === "hidden" ? "Скрыто после жалоб, на проверке" : WORK_STATUS_LABEL[post.status]}</span>}
        {responseCount > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-td-teal px-2 py-0.5 text-[11px] font-semibold text-white">
            {hasNew && <span className="h-1.5 w-1.5 rounded-full bg-td-orange" aria-label="Есть новые" />}Откликов: {responseCount}
          </span>
        )}
        {respondedAt && (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
            <CheckCircle2 className="h-3 w-3" />Отклик отправлен · {fmtDay(respondedAt)}
          </span>
        )}
      </div>
      <p className="mt-2 break-words text-base font-semibold text-td-dark">{post.title}</p>
      <p className="mt-1 text-xs text-td-muted">{post.category}</p>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-td-dark">
        {place && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-td-muted" />{place}</span>}
        <span className="font-semibold">{formatPay(post)}</span>
        {post.kind === "vacancy" && post.employment && <span>{EMPLOYMENT_LABEL[post.employment]}</span>}
        {post.kind === "order" && deadline && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-td-muted" />{deadline}</span>}
      </div>
      <div className="mt-3 flex items-center gap-2 text-xs text-td-muted">
        <Avatar p={author} size={24} />
        <span className="min-w-0 truncate text-td-dark">{author?.full_name ?? "Резидент"}</span>
        <span className="shrink-0">· {daysAgo(post.created_at)}</span>
      </div>
    </div>
  );
}

const isShareable = (p: WorkPost) => p.status === "active" && daysLeft(p.expires_at) > 0;

/* ───────────────────────────── Карточка ───────────────────────────── */

type ResponseRow = { id: string; post_id: string; sender_user_id: string; message: string; created_at: string; link: string | null; contact: string | null };

function PostDialog({ post, author, me, userId, isAdmin, startWithResponses, onClose, onEdit }: {
  post: WorkPost; author?: MiniProfile; me: MyProfile | null; userId: string | null; isAdmin: boolean;
  startWithResponses: boolean; onClose: () => void; onEdit: () => void;
}) {
  const qc = useQueryClient();
  const isAuthor = post.user_id === userId;
  const [showResponses, setShowResponses] = useState(isAuthor && startWithResponses);
  const [respondOpen, setRespondOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [foundOpen, setFoundOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const invalidate = () => qc.invalidateQueries({ queryKey: ["work"] });

  const responses = useQuery({
    queryKey: ["work", "responses", post.id],
    enabled: isAuthor,
    queryFn: async (): Promise<{ rows: ResponseRow[]; people: Record<string, MiniProfile> }> => {
      const { data, error } = await db.from("work_responses").select("*").eq("post_id", post.id).order("created_at", { ascending: false });
      if (error) throw error;
      const rows: ResponseRow[] = data ?? [];
      const ids = Array.from(new Set(rows.map((r) => r.sender_user_id)));
      let people: Record<string, MiniProfile> = {};
      if (ids.length) {
        const { data: ps } = await db.from("profiles").select("user_id, full_name, photo_url, city, username, telegram, social_links").in("user_id", ids);
        people = Object.fromEntries((ps ?? []).map((p: MiniProfile) => [p.user_id, p]));
      }
      return { rows, people };
    },
  });

  const [showMine, setShowMine] = useState(false);
  const myResponse = useQuery({
    queryKey: ["work", "my-response", post.id, userId],
    enabled: !isAuthor && !!userId,
    queryFn: async (): Promise<ResponseRow | null> => {
      const { data, error } = await db.from("work_responses").select("*").eq("post_id", post.id).eq("sender_user_id", userId).maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
  });

  const expertCats = expertCategoriesForWork(post.category, EXPERT_CATEGORIES);
  const experts = useQuery({
    queryKey: ["work", "experts", post.category],
    enabled: post.kind === "order" && post.side === "need" && expertCats.length > 0,
    queryFn: async (): Promise<any[]> => {
      const { data, error } = await db
        .from("profiles")
        .select("user_id, full_name, photo_url, city, telegram, social_links, expert_category, expert_categories, expert_offer, expert_formats, expert_area, expert_hidden")
        .overlaps("expert_categories", expertCats)
        .eq("expert_hidden", false)
        .limit(6);
      if (error) throw error;
      return (data ?? []).filter((p: any) => isExpert(p) && p.user_id !== post.user_id && p.user_id !== userId).slice(0, 3);
    },
  });

  const update = useMutation({
    mutationFn: async (patch: Record<string, unknown>) => {
      const { error } = await db.from("work_posts").update(patch).eq("id", post.id);
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: () => toast.error("Не получилось сохранить. Попробуй ещё раз"),
  });

  const left = daysLeft(post.expires_at);
  const canExtend = left <= 7;
  const expiringSoon = left <= 3;
  const directTg = post.contact_link ? telegramHref(post.contact_link) : null;
  const tgWithText = directTg ? withTextParam(directTg, residentTgText(post, me?.full_name)) : null;
  const tgLabel = post.side === "offer" ? "Написать в Telegram" : post.kind === "vacancy" ? "Отправить резюме в Telegram" : "Написать заказчику в Telegram";
  const tgHint = post.side === "offer" ? null : post.kind === "vacancy"
    ? "Откроется Telegram с готовым сообщением. Прикрепи резюме (скрепка) и отправь"
    : "Откроется Telegram с готовым сообщением";
  const sendViaTelegram = () => {
    if (!tgWithText) return;
    window.open(tgWithText, "_blank", "noopener,noreferrer");
    if (!userId) return;
    const myTg = me?.telegram?.trim() || null;
    void db.from("work_responses")
      .insert({ post_id: post.id, sender_user_id: userId, message: "Откликнулся через Telegram", contact: myTg, link: null })
      .then(({ error }: { error: { code?: string } | null }) => {
        if (error && error.code !== "23505") return;
        qc.invalidateQueries({ queryKey: ["work", "my-response", post.id] });
        qc.invalidateQueries({ queryKey: ["work", "my-responses"] });
      });
  };
  const deadline = formatDeadline(post.deadline);
  const place = placeLabel(post);
  const extend = () =>
    update.mutate({ expires_at: new Date(Date.now() + 30 * 86_400_000).toISOString() }, { onSuccess: () => { invalidate(); toast.success("Продлено на 30 дней"); } });

  const sendConcierge = () => {
    window.open(conciergePostHref(post, me), "_blank", "noopener,noreferrer");
    if (!post.concierge_requested_at) update.mutate({ concierge_requested_at: new Date().toISOString() });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90dvh] w-[calc(100vw-24px)] max-w-lg overflow-y-auto overflow-x-hidden rounded-2xl p-5">
        <DialogHeader className="text-left">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="rounded-full bg-td-teal/10 px-2 py-0.5 text-[11px] font-medium text-td-teal">{post.kind === "vacancy" ? "Вакансия" : "Заказ"}</span>
            {post.status !== "active" && <span className="rounded-full bg-td-orange/10 px-2 py-0.5 text-[11px] font-medium text-td-orange">{WORK_STATUS_LABEL[post.status]}</span>}
          </div>
          <DialogTitle className="break-words pr-6 text-lg text-td-dark">{post.title}</DialogTitle>
          <DialogDescription className="text-xs">{post.category}</DialogDescription>
        </DialogHeader>

        {isAuthor && expiringSoon && post.status === "active" && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-td-orange/10 px-3 py-2 text-sm text-td-dark">
            <span>Срок заканчивается. Ещё актуально?</span>
            <button type="button" className="min-h-9 rounded-full bg-td-orange px-3 text-xs font-semibold text-white" onClick={extend}>Продлить</button>
          </div>
        )}

        <div className="space-y-1.5 text-sm text-td-dark">
          {place && <p className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-td-muted" />{place}</p>}
          <p><span className="text-td-muted">{post.side === "need" ? "Оплата" : "Желаемая оплата"}:</span> <b>{formatPay(post)}</b></p>
          {post.kind === "vacancy" && post.employment && <p><span className="text-td-muted">Занятость:</span> {EMPLOYMENT_LABEL[post.employment]}</p>}
          {deadline && <p><span className="text-td-muted">Срок:</span> {deadline}</p>}
          {post.portfolio_url && (
            <p className="break-all"><span className="text-td-muted">Портфолио:</span>{" "}
              <a href={normalizeHref(post.portfolio_url)} target="_blank" rel="noopener noreferrer" className="text-td-teal underline">{post.portfolio_url}</a>
            </p>
          )}
          {isAuthor && <p className="text-xs text-td-muted">Объявление активно до {new Date(post.expires_at).toLocaleDateString("ru-RU", { day: "numeric", month: "long" })}</p>}
        </div>

        {post.description && <p className="whitespace-pre-line break-words text-sm text-td-dark">{post.description}</p>}

        <Link to="/people/$userId" params={{ userId: post.user_id }} className="flex min-h-11 items-center gap-2 rounded-xl bg-td-soft/60 px-3 py-2">
          <Avatar p={author} size={32} />
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-td-dark">{author?.full_name ?? "Резидент"}</span>
            <span className="block text-xs text-td-muted">{daysAgo(post.created_at)}</span>
          </span>
        </Link>

        {!isAuthor && (
          <div className="flex min-w-0 flex-col gap-2">
            {myResponse.data ? (
              <>
                <div className="min-w-0 rounded-xl bg-td-soft/60 px-3 py-2">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                    <span className="text-sm text-td-dark">Ты откликнулся {fmtDay(myResponse.data.created_at)}</span>
                    <button type="button" className="inline-flex min-h-8 items-center text-xs font-medium text-td-teal underline" onClick={() => setShowMine(!showMine)}>
                      {showMine ? "Скрыть мой отклик" : "Посмотреть мой отклик"}
                    </button>
                  </div>
                  {showMine && (
                    <div className="mt-2 space-y-1 border-t border-td-line pt-2 text-sm text-td-dark">
                      <p className="whitespace-pre-line break-words">{myResponse.data.message}</p>
                      {myResponse.data.link && <a href={normalizeHref(myResponse.data.link)} target="_blank" rel="noopener noreferrer" className="block break-all text-xs text-td-teal underline">{myResponse.data.link}</a>}
                      {myResponse.data.contact && <p className="break-words text-xs text-td-muted">Контакт: {myResponse.data.contact}</p>}
                    </div>
                  )}
                </div>
                {tgWithText && (
                  <a href={tgWithText} target="_blank" rel="noopener noreferrer" className={btnSecondary + " w-full"}>
                    <Send className="h-4 w-4 shrink-0" /><span className="truncate">Написать в Telegram</span>
                  </a>
                )}
              </>
            ) : tgWithText ? (
              <>
                <div className="flex gap-2">
                  {isShareable(post) && <ShareWorkButton postId={post.id} kind={post.kind} title={post.title} variant="square" />}
                  <button type="button" className={btnPrimary + " min-w-0 flex-1"} onClick={sendViaTelegram} disabled={post.status !== "active"}>
                    <Send className="h-4 w-4 shrink-0" /><span className="truncate">{tgLabel}</span>
                  </button>
                </div>
                {tgHint && <p className="text-xs text-td-muted">{tgHint}</p>}
                <button type="button" className="inline-flex min-h-11 items-center justify-center text-sm font-medium text-td-teal underline underline-offset-2 disabled:opacity-50" onClick={() => setRespondOpen(true)} disabled={post.status !== "active"}>
                  Или откликнуться здесь, в приложении
                </button>
              </>
            ) : (
              <div className="flex gap-2">
                {isShareable(post) && <ShareWorkButton postId={post.id} kind={post.kind} title={post.title} variant="square" />}
                <button type="button" className={btnPrimary + " flex-1"} onClick={() => setRespondOpen(true)} disabled={post.status !== "active"}>
                  {post.side === "need" ? "Откликнуться" : "Предложить работу"}
                </button>
              </div>
            )}
            {myResponse.data && isShareable(post) && (
              <div className="flex justify-center"><ShareWorkButton postId={post.id} kind={post.kind} title={post.title} variant="button" /></div>
            )}
          </div>
        )}

        {!isAuthor && (experts.data?.length ?? 0) > 0 && (
          <div className="rounded-xl border border-td-line p-3">
            <p className="mb-2 text-sm font-semibold text-td-dark">Эксперты по теме</p>
            <div className="flex flex-col gap-2">
              {experts.data!.map((e) => {
                const c = primaryContact(e);
                return (
                  <div key={e.user_id} className="flex items-center gap-2">
                    <Avatar p={e} size={32} />
                    <Link to="/people/$userId" params={{ userId: e.user_id }} className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-td-dark">{e.full_name}</span>
                      <span className="block truncate text-xs text-td-muted">{e.expert_offer}</span>
                    </Link>
                    {c && <a href={c.href} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-9 shrink-0 items-center rounded-full border border-td-teal/40 px-3 text-xs font-medium text-td-teal">Написать</a>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {isAuthor && (
          <div className="space-y-2">
            <button type="button" className={btnPrimary + " w-full"} onClick={() => setShowResponses(!showResponses)}>
              <Users className="h-4 w-4" /> Отклики ({responses.data?.rows.length ?? 0})
            </button>
            {showResponses && <ResponsesList post={post} data={responses.data} loading={responses.isLoading} authorName={me?.full_name} />}
            {isShareable(post) && (
              <div className="flex justify-center">
                <ShareWorkButton postId={post.id} kind={post.kind} title={post.title} variant="button" />
              </div>
            )}
            {post.status === "active" && (
              <div className="grid grid-cols-2 gap-2">
                <button type="button" className={btnSecondary} onClick={() => setFoundOpen(true)}>{post.kind === "order" ? "Нашёл исполнителя" : "Нашёл человека"}</button>
                <button type="button" className={btnSecondary} onClick={onEdit}>Редактировать</button>
                {canExtend && <button type="button" className={btnSecondary} onClick={extend}>Продлить на 30 дней</button>}
                <button type="button" className={btnSecondary} onClick={() => setCloseOpen(true)}>Закрыть</button>
              </div>
            )}
            {post.status !== "active" && post.status !== "hidden" && canExtend && (
              <button type="button" className={btnSecondary + " w-full"} onClick={extend}>Продлить на 30 дней</button>
            )}
            <button type="button" className={btnSecondary + " w-full"} style={privePalette} onClick={sendConcierge}>
              <ShieldCheck className="h-4 w-4 shrink-0" /><span className="truncate">{post.concierge_requested_at ? "Заявка в PRIVE ✓" : "Передать в PRIVE · платно"}</span>
            </button>
            <p className="text-center text-xs text-td-muted">Платная услуга: стоимость обсудим в Telegram</p>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-td-line pt-3">
          {!isAuthor ? (
            <button type="button" className="inline-flex min-h-11 items-center gap-1 text-xs text-td-muted hover:text-td-dark" onClick={() => setReportOpen(true)}>
              <Flag className="h-3.5 w-3.5" /> Пожаловаться
            </button>
          ) : <span />}
          {isAdmin && (
            post.status === "hidden" ? (
              <button type="button" className="min-h-11 rounded-full px-3 text-xs font-semibold text-td-teal" onClick={() => update.mutate({ status: "active" }, { onSuccess: () => { invalidate(); toast.success("Объявление возвращено"); } })}>Вернуть</button>
            ) : (
              <button type="button" className="min-h-11 rounded-full px-3 text-xs font-semibold text-red-600" onClick={() => update.mutate({ status: "hidden" }, { onSuccess: () => { invalidate(); toast.success("Объявление скрыто"); } })}>Скрыть</button>
            )
          )}
        </div>

        {respondOpen && <RespondDialog post={post} author={author} me={me} userId={userId} onClose={() => setRespondOpen(false)} />}
        {reportOpen && <ReportDialog post={post} userId={userId} onClose={() => setReportOpen(false)} />}
        {foundOpen && (
          <FoundDialog
            post={post}
            responders={(responses.data?.rows ?? []).map((r) => responses.data!.people[r.sender_user_id]).filter(Boolean)}
            onClose={() => setFoundOpen(false)}
            onDone={(uid) => update.mutate({ status: "found", found_user_id: uid, found_at: new Date().toISOString() }, {
              onSuccess: () => { invalidate(); setFoundOpen(false); toast.success("Спасибо! Карточка закроется для других"); },
            })}
          />
        )}
        {closeOpen && (
          <Dialog open onOpenChange={(o) => !o && setCloseOpen(false)}>
            <DialogContent className="w-[calc(100vw-24px)] max-w-sm rounded-2xl">
              <DialogHeader className="text-left">
                <DialogTitle>Закрыть объявление?</DialogTitle>
                <DialogDescription>Его больше не увидят другие. Ты сможешь найти его в «Мои».</DialogDescription>
              </DialogHeader>
              <div className="flex gap-2">
                <button type="button" className={btnSecondary + " flex-1"} onClick={() => setCloseOpen(false)}>Отмена</button>
                <button type="button" className={btnPrimary + " flex-1"} onClick={() => update.mutate({ status: "closed" }, { onSuccess: () => { invalidate(); setCloseOpen(false); toast.success("Объявление закрыто"); } })}>Закрыть</button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}

const firstName = (name?: string | null) => (name ?? "").trim().split(/\s+/)[0] || "автору";

function fmtDay(iso: string) {
  const d = new Date(iso);
  const t = new Date();
  if (d.toDateString() === t.toDateString()) return "сегодня";
  return d.toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
}

/** Ссылка: добавляем https:// и проверяем, что похоже на адрес. */
function normalizeLink(raw: string): { value: string | null; ok: boolean } {
  const s = raw.trim();
  if (!s) return { value: null, ok: true };
  const v = /^https?:\/\//i.test(s) ? s : `https://${s}`;
  try {
    const u = new URL(v);
    return { value: v, ok: u.hostname.includes(".") && !/\s/.test(s) };
  } catch {
    return { value: v, ok: false };
  }
}

/** Telegram-ссылка из поля контакта отклика (@ник, ник или t.me). */
function contactTelegram(raw?: string | null): string | null {
  const s = raw?.trim();
  if (!s) return null;
  if (/t\.me|telegram/i.test(s) || s.startsWith("@")) return telegramHref(s);
  return null;
}

function workOrigin() {
  return typeof window !== "undefined" ? window.location.origin : "https://turkey-drive.com";
}

/** Готовый текст от резидента автору объявления. */
function residentTgText(post: Pick<WorkPost, "id" | "kind" | "side" | "title">, myName?: string | null): string {
  const url = `${workOrigin()}/w/${post.id}`;
  const n = myName?.trim();
  const hello = n ? `Привет! Я ${n}, резидент сообщества TurkeyDrive (turkey-drive.com).` : "Привет! Я резидент сообщества TurkeyDrive (turkey-drive.com).";
  if (post.side === "offer") return `${hello} Увидел(а) твоё объявление «${post.title}»: ${url}\n\nХочу предложить: `;
  if (post.kind === "order") return `${hello} Пишу по заказу «${post.title}»: ${url}\n\nГотов(а) обсудить условия и сроки. Коротко о себе: `;
  return `${hello} Пишу по вакансии «${post.title}»: ${url}\n\nМоё резюме прикрепляю к этому сообщению.\nКоротко о себе: `;
}

/** Готовый текст от автора откликнувшемуся. */
function authorTgText(post: Pick<WorkPost, "id" | "title">, authorName?: string | null): string {
  const n = authorName?.trim() || "автор объявления";
  return `Привет! Это ${n} из TurkeyDrive. Увидел(а) твой отклик на «${post.title}» (${workOrigin()}/w/${post.id}). Давай обсудим: `;
}

function ResponsesList({ post, data, loading, authorName }: { post: WorkPost; data?: { rows: ResponseRow[]; people: Record<string, MiniProfile> }; loading: boolean; authorName?: string | null }) {
  if (loading) return <div className="h-20 animate-pulse rounded-xl bg-td-soft" />;
  if (!data || data.rows.length === 0) return <p className="rounded-xl bg-td-soft/60 px-3 py-4 text-center text-sm text-td-muted">Пока никто не откликнулся</p>;
  return (
    <div className="flex flex-col gap-2">
      <p className="rounded-xl bg-td-teal/10 px-3 py-2 text-sm text-td-dark">
        Откликов: {data.rows.length}. Свяжись с теми, кто понравился. Когда найдёшь человека, нажми «{post.kind === "order" ? "Нашёл исполнителя" : "Нашёл человека"}»: объявление закроется.
      </p>
      {data.rows.map((r) => {
        const p = data.people[r.sender_user_id];
        const tg = contactTelegram(r.contact) ?? telegramHref(p?.telegram);
        const other = !tg ? primaryContact(p) : null;
        const text = authorTgText(post, authorName);
        const href = tg ? withTextParam(tg, text) : other?.href ?? null;
        const resume = r.link ? normalizeHref(r.link) : null;
        const plainContact = r.contact && !contactTelegram(r.contact) ? r.contact : null;
        return (
          <div key={r.id} className="min-w-0 rounded-xl border border-td-line p-3">
            <div className="flex items-center gap-2">
              <Avatar p={p} size={32} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-td-dark">{p?.full_name ?? "Резидент"}</p>
                <p className="truncate text-xs text-td-muted">{[p?.city, fmtDay(r.created_at)].filter(Boolean).join(" · ")}</p>
              </div>
            </div>
            <p className="mt-2 whitespace-pre-line break-words text-sm text-td-dark">{r.message}</p>
            {resume && (
              <a href={resume} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex h-9 max-w-full items-center gap-1.5 rounded-full bg-td-soft px-3 text-xs font-semibold text-td-teal">
                <ExternalLink className="h-3.5 w-3.5 shrink-0" /><span className="truncate">Открыть резюме</span>
              </a>
            )}
            {plainContact && <p className="mt-2 break-words text-xs text-td-dark"><span className="text-td-muted">Контакт:</span> {plainContact}</p>}
            <div className="mt-2 flex items-center gap-2">
              {href && <a href={href} target="_blank" rel="noopener noreferrer" className={btnPrimary + " min-w-0 flex-1"}><Send className="h-4 w-4 shrink-0" /><span className="truncate">{tg ? "Написать в Telegram" : "Написать"}</span></a>}
              <Link to="/people/$userId" params={{ userId: r.sender_user_id }} className={btnSecondary + (href ? "" : " flex-1")}>Профиль</Link>
            </div>
            {!href && !plainContact && <p className="mt-1 text-xs text-td-muted">Ответь через профиль: контакт не указан</p>}
          </div>
        );
      })}
    </div>
  );
}

function RespondDialog({ post, author, me, userId, onClose }: { post: WorkPost; author?: MiniProfile; me: MyProfile | null; userId: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const need = post.side === "need";
  const myTg = me?.telegram?.trim() ? (me.telegram.trim().startsWith("@") || /t\.me/i.test(me.telegram) ? me.telegram.trim() : `@${me.telegram.trim()}`) : "";
  const [msg, setMsg] = useState("");
  const [link, setLink] = useState("");
  const [contact, setContact] = useState(myTg);
  const [linkTouched, setLinkTouched] = useState(false);
  const [sent, setSent] = useState(false);
  const linkN = normalizeLink(link);
  const name = firstName(author?.full_name);
  const fullName = author?.full_name ?? "Автор";
  const send = useMutation({
    mutationFn: async () => {
      const { error } = await db.from("work_responses").insert({
        post_id: post.id, sender_user_id: userId, message: msg.trim(),
        link: linkN.value, contact: contact.trim() || null,
      });
      if (error && error.code !== "23505") throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["work", "my-response", post.id] });
      qc.invalidateQueries({ queryKey: ["work", "my-responses"] });
      setSent(true);
    },
    onError: () => toast.error("Не получилось отправить отклик. Попробуй ещё раз"),
  });
  const authorTg = post.contact_link ? telegramHref(post.contact_link) : null;
  const tgText = residentTgText(post, me?.full_name);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90dvh] w-[calc(100vw-24px)] max-w-md content-start overflow-y-auto overflow-x-hidden rounded-2xl">
        {sent ? (
          <div className="flex min-w-0 flex-col items-center text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-td-teal/15">
              <CheckCircle2 className="h-8 w-8 text-td-teal" />
            </span>
            <DialogTitle className="mt-3 text-lg text-td-dark">Отклик отправлен</DialogTitle>
            <DialogDescription className="mt-2 text-sm">
              {fullName} увидит его в разделе «Работа» и получит уведомление. Ответ придёт тебе в уведомления или в Telegram.
            </DialogDescription>
            <div className="mt-4 flex w-full flex-col gap-2">
              {authorTg && (
                <a href={withTextParam(authorTg, tgText)} target="_blank" rel="noopener noreferrer" className={btnPrimary + " w-full"}>
                  <Send className="h-4 w-4 shrink-0" /><span className="truncate">Написать {name} в Telegram</span>
                </a>
              )}
              <button type="button" className={btnSecondary + " w-full"} onClick={onClose}>Готово</button>
            </div>
          </div>
        ) : (
          <>
            <DialogHeader className="min-w-0 text-left">
              <DialogTitle>{need ? (post.kind === "order" ? "Отклик на заказ" : "Отклик на вакансию") : "Предложить работу"}</DialogTitle>
              <DialogDescription className="line-clamp-2 break-words">«{post.title}»</DialogDescription>
            </DialogHeader>
            <p className="rounded-xl bg-td-teal/10 px-3 py-2 text-xs text-td-dark">
              {need
                ? `${fullName} получит твой отклик в приложении и увидит твой профиль и контакты. Резюме не обязательно: достаточно пары предложений и ссылки на работы`
                : `${fullName} получит твоё предложение в приложении и увидит твой профиль и контакты`}
            </p>
            <div className="min-w-0 space-y-1">
              <label className="text-sm font-medium text-td-dark" htmlFor="wr-msg">{need ? "Сообщение автору" : "Что предлагаешь"}</label>
              <Textarea id="wr-msg" rows={4} maxLength={1500} value={msg} onChange={(e) => setMsg(e.target.value)} className="h-28 resize-none"
                placeholder={need ? "Привет! Я Анна, 3 года веду SMM для бьюти-проектов. Могу начать с понедельника. Примеры работ ниже" : "Опиши задачу и условия: что нужно сделать, сроки, оплата"} />
              <div className="flex items-start justify-between gap-2 text-xs text-td-muted">
                <span className="min-w-0">{need ? "2–4 предложения: кто ты, какой у тебя опыт, когда можешь начать" : "Коротко: задача, сроки, условия"}</span>
                <span className="shrink-0 text-[11px]">{msg.length}/1500</span>
              </div>
            </div>
            <div className="min-w-0 space-y-1">
              <label className="text-sm font-medium text-td-dark" htmlFor="wr-link">{need ? "Резюме или портфолио (ссылка)" : "Ссылка на описание"} <span className="font-normal text-td-muted">— необязательно</span></label>
              <Input id="wr-link" inputMode="url" maxLength={500} value={link} onChange={(e) => setLink(e.target.value)} onBlur={() => setLinkTouched(true)} placeholder="https://" />
              {linkTouched && !linkN.ok ? (
                <p className="text-xs text-red-600">Похоже, это не ссылка. Проверь адрес, например drive.google.com/…</p>
              ) : need ? (
                <p className="text-xs text-td-muted">Резюме удобнее отправить файлом в Telegram, здесь можно оставить ссылку: Google Drive, Notion, Behance, сайт или Instagram</p>
              ) : null}
            </div>
            <div className="min-w-0 space-y-1">
              <label className="text-sm font-medium text-td-dark" htmlFor="wr-contact">Как с тобой связаться <span className="font-normal text-td-muted">— необязательно</span></label>
              <Input id="wr-contact" maxLength={200} value={contact} onChange={(e) => setContact(e.target.value)} placeholder="@ник в Telegram или телефон" />
              {!myTg && !contact.trim() && <p className="text-xs text-td-muted">Добавь Telegram или телефон, иначе автор сможет ответить только в приложении</p>}
            </div>
            <div className="flex gap-2">
              <button type="button" className={btnSecondary + " flex-1"} onClick={onClose}>Отмена</button>
              <button type="button" className={btnPrimary + " flex-1"} disabled={!msg.trim() || !linkN.ok || send.isPending} onClick={() => send.mutate()}>
                {need ? "Отправить отклик" : "Отправить"}
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReportDialog({ post, userId, onClose }: { post: WorkPost; userId: string | null; onClose: () => void }) {
  const [reason, setReason] = useState("");
  const send = useMutation({
    mutationFn: async () => {
      const { error } = await db.from("work_reports").insert({ post_id: post.id, reporter_user_id: userId, reason: reason.trim() || null });
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Спасибо, мы посмотрим"); onClose(); },
    onError: (e: any) => { if (e?.code === "23505") { toast("Ты уже жаловался(лась)"); onClose(); } else toast.error("Не получилось отправить жалобу"); },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100vw-24px)] max-w-md content-start rounded-2xl">
        <DialogHeader className="text-left">
          <DialogTitle>Пожаловаться</DialogTitle>
          <DialogDescription>Что не так с объявлением? Можно не объяснять.</DialogDescription>
        </DialogHeader>
        <Textarea rows={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} className="h-24 resize-none" placeholder="Причина (необязательно)" />
        <div className="flex gap-2">
          <button type="button" className={btnSecondary + " flex-1"} onClick={onClose}>Отмена</button>
          <button type="button" className={btnPrimary + " flex-1"} disabled={send.isPending} onClick={() => send.mutate()}>Отправить</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FoundDialog({ post, responders, onClose, onDone }: { post: WorkPost; responders: MiniProfile[]; onClose: () => void; onDone: (uid: string | null) => void }) {
  const [choice, setChoice] = useState<string>("elsewhere");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90dvh] w-[calc(100vw-24px)] max-w-md content-start overflow-y-auto rounded-2xl">
        <DialogHeader className="text-left">
          <DialogTitle>{post.kind === "order" ? "Нашёл исполнителя" : "Нашёл человека"}</DialogTitle>
          <DialogDescription>Кто помог? Выбери из откликнувшихся или отметь, что нашёл в другом месте.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          {responders.map((p) => (
            <label key={p.user_id} className="flex min-h-11 cursor-pointer items-center gap-2 rounded-xl border border-td-line px-3">
              <input type="radio" name="found" checked={choice === p.user_id} onChange={() => setChoice(p.user_id)} />
              <Avatar p={p} size={24} /><span className="truncate text-sm">{p.full_name}</span>
            </label>
          ))}
          <label className="flex min-h-11 cursor-pointer items-center gap-2 rounded-xl border border-td-line px-3">
            <input type="radio" name="found" checked={choice === "elsewhere"} onChange={() => setChoice("elsewhere")} />
            <span className="text-sm">Нашёл в другом месте</span>
          </label>
        </div>
        <div className="flex gap-2">
          <button type="button" className={btnSecondary + " flex-1"} onClick={onClose}>Отмена</button>
          <button type="button" className={btnPrimary + " flex-1"} onClick={() => onDone(choice === "elsewhere" ? null : choice)}>Готово</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ───────────────────────────── Форма ───────────────────────────── */

const TYPE_OPTIONS: { kind: WorkKind; side: WorkSide; title: string; hint: string; Icon: typeof Briefcase }[] = [
  { kind: "vacancy", side: "need", title: "Нужен человек на постоянно", hint: "Няня, помощница, SMM, администратор", Icon: UserSearch },
  { kind: "order", side: "need", title: "Нужно сделать разовое дело", hint: "Перевод, ремонт, сайт, сопровождение", Icon: Wrench },
];

const TITLE_PLACEHOLDER: Record<string, string> = {
  "vacancy-need": "Нужна няня для ребёнка 5 месяцев, Анталья",
  "order-need": "Нужен перевод документов на турецкий",
  "vacancy-offer": "Администратор, полный день",
  "order-offer": "Делаю сайты и лендинги",
};

function PostForm({ me, initial, onCancel, onSaved }: { me: MyProfile | null; initial: WorkPost | null; onCancel: () => void; onSaved: (p: WorkPost, created: boolean) => void }) {
  const qc = useQueryClient();
  const [type, setType] = useState<{ kind: WorkKind; side: WorkSide } | null>(initial ? { kind: initial.kind, side: initial.side } : null);
  const [title, setTitle] = useState(initial?.title ?? "");
  const [category, setCategory] = useState(initial?.category ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [city, setCity] = useState(initial?.city ?? "");
  const [isOnline, setIsOnline] = useState(initial?.is_online ?? false);
  const [employment, setEmployment] = useState(initial?.employment ?? "full");
  const [amount, setAmount] = useState(initial?.pay_amount != null ? String(initial.pay_amount) : "");
  const [currency, setCurrency] = useState(initial?.pay_currency ?? "TRY");
  const [period, setPeriod] = useState(initial?.pay_period ?? "");
  const [deadline, setDeadline] = useState(initial?.deadline ?? "");
  const [portfolio, setPortfolio] = useState(initial?.portfolio_url ?? "");
  const [contact, setContact] = useState(initial?.contact_link ?? "");
  const [prefilled, setPrefilled] = useState(!!initial);

  useEffect(() => {
    if (prefilled || !me) return;
    if (!city && me.city) setCity(me.city);
    if (!contact && me.telegram) setContact(me.telegram);
    setPrefilled(true);
  }, [me, prefilled]); // eslint-disable-line react-hooks/exhaustive-deps

  const today = new Date().toISOString().slice(0, 10);
  const effPeriod = period || (type?.kind === "vacancy" ? "month" : "project");

  const save = useMutation({
    mutationFn: async (): Promise<WorkPost> => {
      const payload = {
        kind: type!.kind,
        side: type!.side,
        title: title.trim(),
        category,
        description: description.trim() || null,
        city: city.trim() || null,
        is_online: isOnline,
        employment: type!.kind === "vacancy" ? employment : null,
        pay_amount: effPeriod === "negotiable" || amount.trim() === "" ? null : Number(amount),
        pay_currency: currency,
        pay_period: effPeriod,
        deadline: type!.kind === "order" && deadline ? deadline : null,
        portfolio_url: type!.side === "offer" ? portfolio.trim() || null : null,
        contact_link: contact.trim() || null,
      };
      if (initial) {
        const { data, error } = await db.from("work_posts").update(payload).eq("id", initial.id).select("*").single();
        if (error) throw error;
        return data;
      }
      const { data: u } = await supabase.auth.getUser();
      const { data, error } = await db.from("work_posts").insert({ ...payload, user_id: u.user?.id }).select("*").single();
      if (error) throw error;
      return data;
    },
    onSuccess: (p) => { qc.invalidateQueries({ queryKey: ["work"] }); if (initial) toast.success("Изменения сохранены"); onSaved(p, !initial); },
    onError: () => toast.error("Не получилось опубликовать. Проверь поля и попробуй ещё раз"),
  });

  const submit = () => {
    if (title.trim().length < 3) return toast.error("Заголовок — хотя бы 3 символа");
    if (!category) return toast.error("Выбери сферу");
    if (!city.trim() && !isOnline) return toast.error("Укажи город или отметь «Онлайн»");
    if (amount.trim() && (isNaN(Number(amount)) || Number(amount) < 0)) return toast.error("Сумма — число от 0");
    if (deadline && deadline < today && !initial) return toast.error("Срок не может быть раньше сегодняшнего дня");
    save.mutate();
  };

  if (!type) {
    return (
      <main className="mx-auto w-full max-w-2xl min-w-0 px-4 py-6">
        <button type="button" onClick={onCancel} className="mb-3 inline-flex min-h-11 items-center gap-1 text-sm text-td-muted"><ArrowLeft className="h-4 w-4" /> Назад</button>
        <h1 className="text-2xl font-bold text-td-dark">Что ты хочешь?</h1>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {TYPE_OPTIONS.map((o) => (
            <div key={o.title}>
              <button type="button" onClick={() => setType({ kind: o.kind, side: o.side })}
                className="flex w-full items-start gap-3 rounded-2xl border border-td-line bg-white p-4 text-left transition hover:border-td-teal">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-td-teal/10 text-td-teal"><o.Icon className="h-5 w-5" /></span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-td-dark">{o.title}</span>
                  <span className="mt-0.5 block text-xs text-td-muted">{o.hint}</span>
                </span>
              </button>
            </div>
          ))}
        </div>
        <div className="mt-5 flex flex-col gap-1 text-sm">
          <Link to="/events/new" className="inline-flex min-h-10 items-center text-td-teal">Позвать на мероприятие → Афиша</Link>
          <Link to="/requests" className="inline-flex min-h-10 items-center text-td-teal">Задать вопрос сообществу → Спросить своих</Link>
        </div>
      </main>
    );
  }

  const key = `${type.kind}-${type.side}`;
  const label = "mb-1 block text-sm font-medium text-td-dark";
  return (
    <main className="mx-auto w-full max-w-2xl min-w-0 px-4 py-6">
      <button type="button" onClick={() => (initial ? onCancel() : setType(null))} className="mb-3 inline-flex min-h-11 items-center gap-1 text-sm text-td-muted"><ArrowLeft className="h-4 w-4" /> Назад</button>
      <h1 className="text-xl font-bold text-td-dark">{TYPE_OPTIONS.find((o) => o.kind === type.kind && o.side === type.side)?.title}</h1>
      <div className="mt-4 flex flex-col gap-4">
        <div>
          <label className={label}>Заголовок *</label>
          <Input maxLength={140} value={title} onChange={(e) => setTitle(e.target.value)} placeholder={TITLE_PLACEHOLDER[key]} />
        </div>
        <div>
          <label className={label}>Сфера *</label>
          <Select value={category || undefined} onValueChange={setCategory}>
            <SelectTrigger><SelectValue placeholder="Выбери сферу" /></SelectTrigger>
            <SelectContent>
              {category && !(WORK_CATEGORIES as readonly string[]).includes(category) && <SelectItem value={category}>{category}</SelectItem>}
              {WORK_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="mt-1 text-xs text-td-muted">Няня, помощница по дому, повар → «Няни и дети» или «Помощь по дому и быт»</p>
        </div>
        <div>
          <label className={label}>Описание</label>
          <Textarea rows={5} maxLength={3000} value={description} onChange={(e) => setDescription(e.target.value)} />
          <p className="mt-1 text-right text-xs text-td-muted">{description.length}/3000</p>
        </div>
        <div>
          <label className={label}>Город</label>
          <CitySelect value={city} onChange={setCity} allTurkeyLabel="Без города" />
          <label className="mt-2 flex min-h-11 cursor-pointer items-center gap-2 text-sm text-td-dark">
            <input type="checkbox" className="h-5 w-5" checked={isOnline} onChange={(e) => setIsOnline(e.target.checked)} /> Можно удалённо / онлайн
          </label>
        </div>
        {type.kind === "vacancy" && (
          <div>
            <label className={label}>Формат занятости</label>
            <Select value={employment} onValueChange={setEmployment}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(EMPLOYMENT_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div>
          <label className={label}>{type.side === "need" ? "Оплата" : "Желаемая оплата"}</label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <Select value={effPeriod} onValueChange={setPeriod}>
              <SelectTrigger className="col-span-2 sm:col-span-1"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(PAY_PERIOD_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
            {effPeriod !== "negotiable" && (
              <>
                <Input inputMode="decimal" type="number" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Сумма" />
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </>
            )}
          </div>
        </div>
        {type.kind === "order" && (
          <div>
            <label className={label}>Срок</label>
            <Input type="date" min={today} value={deadline} onChange={(e) => setDeadline(e.target.value)} />
          </div>
        )}
        {type.side === "offer" && (
          <div>
            <label className={label}>Ссылка на портфолио или Instagram</label>
            <Input value={portfolio} onChange={(e) => setPortfolio(e.target.value)} placeholder="instagram.com/…" />
          </div>
        )}
        <div>
          <label className={label}>Контакт для прямой связи</label>
          <Input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Telegram или ссылка" />
        </div>
        <p className="rounded-xl bg-td-soft/60 px-3 py-2 text-xs text-td-muted">
          Без рекламы (для неё есть Афиша и «Мои услуги»). Не просите предоплату за трудоустройство. Телефоны и адреса лучше не писать в тексте: люди напишут через кнопку.
        </p>
        <button type="button" className={btnPrimary} disabled={save.isPending} onClick={submit}>
          {initial ? "Сохранить" : "Опубликовать"}
        </button>
      </div>
    </main>
  );
}

function SuccessScreen({ post, me, onMine, onAnother }: { post: WorkPost; me: MyProfile | null; onMine: () => void; onAnother: () => void }) {
  const qc = useQueryClient();
  const [sent, setSent] = useState(!!post.concierge_requested_at);
  const concierge = () => {
    setSent(true);
    window.open(conciergePostHref(post, me), "_blank", "noopener,noreferrer");
    void db.from("work_posts").update({ concierge_requested_at: new Date().toISOString() }).eq("id", post.id).is("concierge_requested_at", null)
      .then(() => qc.invalidateQueries({ queryKey: ["work"] }));
  };
  return (
    <main className="mx-auto flex w-full max-w-md flex-col items-center px-4 py-10 text-center">
      <MrDriveAvatar variant="requests" size={112} />
      <h1 className="mt-5 text-2xl font-bold text-td-dark">Опубликовано!</h1>
      <p className="mt-2 text-sm text-td-muted">Свои уже видят твоё объявление. Отклики придут в уведомления.</p>
      <div className="mt-6 flex w-full flex-col gap-2">
        <button type="button" className={btnPrimary} onClick={onMine}>Смотреть мои объявления</button>
        <button type="button" className={btnSecondary} onClick={onAnother}>Ещё одно</button>
        <div className="flex gap-2">
          <button type="button" className={btnSecondary + " flex-1"} style={privePalette} onClick={concierge}><ShieldCheck className="h-4 w-4 shrink-0" /><span className="truncate">{sent ? "Заявка в PRIVE ✓" : "Передать в PRIVE · платно"}</span></button>
          {isShareable(post) && <ShareWorkButton postId={post.id} kind={post.kind} title={post.title} variant="square" />}
        </div>
        <p className="text-xs text-td-muted">Платная услуга: стоимость обсудим в Telegram</p>
      </div>
    </main>
  );
}

function WorkHint({ kind }: { kind: WorkKind }) {
  const key = kind === "vacancy" ? "work-hint-vacancies" : "work-hint-orders";
  const [hidden, setHidden] = useState(true);
  useEffect(() => {
    try { setHidden(window.localStorage.getItem(key) === "1"); } catch { setHidden(false); }
  }, [key]);
  if (hidden) return null;
  const close = () => { setHidden(true); try { window.localStorage.setItem(key, "1"); } catch { /* ignore */ } };
  return (
    <div className="relative mt-3 rounded-2xl bg-td-soft p-3 pr-10 text-sm text-td-muted">
      {kind === "vacancy" ? (
        <>Ищешь работу? Откликайся на вакансии: резюме отправляй автору прямо в Telegram. А{" "}
          <Link to="/profile" search={{ section: "interests" } as any} className="font-medium text-td-teal underline">в профиле</Link>{" "}
          отметь «Работу» в интересах</>
      ) : (
        <>Нужен исполнитель? Загляни в{" "}
          <Link to="/people" search={{ experts: true } as any} className="font-medium text-td-teal underline">«Эксперты»</Link>: там специалисты с услугами и ценами</>
      )}
      <button type="button" aria-label="Закрыть подсказку" onClick={close} className="absolute right-1.5 top-1.5 flex h-8 w-8 items-center justify-center rounded-full text-td-muted hover:text-td-dark">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
